import { skillStore, skillDef, availableSkillIds, SKILL_SLOTS } from "../items/skillStore.js";
import { weaponStore } from "../items/weaponStore.js";
import { drawItemIcon } from "../items/itemArt.js";
import { injectTheme } from "./theme.js";

/**
 * Bottom-left skill bar — 4 slots (keys 1-4) sitting just ABOVE the hunger/thirst vitals. Filled
 * slots show their number + the skill icon; clicking one (or pressing its number) activates it.
 * Edited in the Skills page (K). Reflects `skillStore`.
 */

const STYLE_ID = "ghostland-skillbar-style";
const CSS = `
#gl-skillbar {
  position: fixed; left: 16px; bottom: 64px; z-index: 25;
  display: flex; gap: 8px;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
}
.gl-skill-cell {
  position: relative; width: 52px; height: 52px; border-radius: 8px;
  background: rgba(8,4,12,.55); border: 2px solid var(--gl-border);
  display: flex; align-items: center; justify-content: center;
}
.gl-skill-cell.filled { border-color: var(--gl-border-focus); cursor: pointer; background: rgba(8,4,12,.8); }
.gl-skill-cell.filled:hover { border-color: var(--gl-accent, #b48cff); }
.gl-skill-cell.locked { opacity: .38; filter: grayscale(1); cursor: default; }
.gl-skill-cell canvas { image-rendering: pixelated; width: 36px; height: 36px; pointer-events: none; }
.gl-skill-key {
  position: absolute; top: 1px; left: 3px; font-size: 10px; line-height: 1;
  color: var(--gl-muted); font-weight: 700;
}
.gl-skill-cell.filled .gl-skill-key { color: #ffd23f; text-shadow: 0 1px 2px #000; }
/* WoW-style cooldown: a dark radial sweep over the icon + the seconds remaining. */
.gl-skill-cd {
  position: absolute; inset: 0; border-radius: 6px; pointer-events: none; display: none;
  align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 13px;
  text-shadow: 0 1px 2px #000;
}
.gl-skill-cd.on { display: flex; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS;
  document.head.appendChild(s);
}

let root: HTMLDivElement | null = null;
let onActivate: ((index: number) => void) | null = null;

/** Active cooldowns keyed by skillId: { start (ms), dur (ms) }. Drives the WoW-style slot sweep. */
const cooldowns = new Map<string, { start: number; dur: number }>();
/** Per-slot DOM: the skillId shown there + its cooldown overlay element (rebuilt by render). */
let slotIds: (string | null)[] = [];
let slotCds: (HTMLDivElement | null)[] = [];
let cdRaf = 0;

function render(): void {
  if (!root) return;
  const slots = skillStore.get();
  const { hand1, hand2 } = weaponStore.get();
  const available = new Set(availableSkillIds(hand1, hand2)); // deduped, gear-gated
  root.innerHTML = "";
  slotIds = []; slotCds = [];
  for (let i = 0; i < SKILL_SLOTS; i++) {
    const def = skillDef(slots[i]);
    const usable = def ? available.has(def.id) : false;
    const cell = document.createElement("div");
    cell.className = "gl-skill-cell" + (def ? " filled" : "") + (def && !usable ? " locked" : "");
    const key = document.createElement("span");
    key.className = "gl-skill-key"; key.textContent = String(i + 1);
    cell.appendChild(key);
    let cd: HTMLDivElement | null = null;
    if (def) {
      cell.appendChild(drawItemIcon(def.icon));
      cell.title = usable ? `${def.name} (press ${i + 1})` : `${def.name} — needs a weapon with this skill`;
      if (usable) cell.addEventListener("click", () => onActivate?.(i));
      cd = document.createElement("div"); cd.className = "gl-skill-cd"; cell.appendChild(cd);
    }
    slotIds.push(def?.id ?? null); slotCds.push(cd);
    root!.appendChild(cell);
  }
  paintCooldowns(); // re-apply any active sweeps onto the fresh cells
}

/** Repaint every slot's cooldown sweep (called each frame while any cooldown runs). */
function paintCooldowns(): void {
  const now = performance.now();
  for (let i = 0; i < slotIds.length; i++) {
    const id = slotIds[i], el = slotCds[i];
    if (!el) continue;
    const c = id ? cooldowns.get(id) : undefined;
    const remain = c ? c.start + c.dur - now : 0;
    if (!c || remain <= 0) { el.classList.remove("on"); el.style.background = ""; el.textContent = ""; continue; }
    const frac = remain / c.dur;                       // 1 → 0
    el.classList.add("on");
    el.style.background = `conic-gradient(rgba(6,4,10,.66) ${frac * 360}deg, transparent 0deg)`;
    el.textContent = remain >= 1000 ? String(Math.ceil(remain / 1000)) : remain.toFixed(1).replace(/\.0$/, "");
  }
}

function tickCooldowns(): void {
  const now = performance.now();
  for (const [id, c] of cooldowns) if (now >= c.start + c.dur) cooldowns.delete(id);
  paintCooldowns();
  cdRaf = cooldowns.size ? requestAnimationFrame(tickCooldowns) : 0;
}

/** Start a WoW-style cooldown sweep on the slot bound to `skillId` (does nothing if it isn't slotted). */
export function setSkillCooldown(skillId: string, durationMs: number): void {
  if (durationMs <= 0) return;
  cooldowns.set(skillId, { start: performance.now(), dur: durationMs });
  if (!cdRaf) cdRaf = requestAnimationFrame(tickCooldowns);
}

/** Mount the skill bar (idempotent). `activate(index)` fires when a filled slot is clicked. */
export function mountSkillBar(activate: (index: number) => void): void {
  ensureStyle();
  onActivate = activate;
  if (!root) {
    root = document.createElement("div");
    root.id = "gl-skillbar";
    document.body.appendChild(root);
    skillStore.subscribe(() => render());
    weaponStore.subscribe(() => render()); // lock/unlock slots as gear changes
  }
}
